import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import AiAnswer from './AiAnswer';

type Preset = { id: string; label: string };
type ContextKind = 'document' | 'task' | 'meeting' | 'project' | 'free';

const GROUP_PREFIX: Record<ContextKind, string> = {
  document: 'doc.',
  task: 'task.',
  meeting: 'meet.',
  project: 'proj.',
  free: '',
};

export default function AiContextPanel({
  contextLabel,
  contextKind,
  projectId,
  documentId,
}: {
  contextLabel: string;
  contextKind: ContextKind;
  projectId?: number;
  documentId?: number;
}) {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [prompt, setPrompt] = useState('');
  const [answer, setAnswer] = useState<{
    text: string;
    title: string;
    costUsd: number;
    attached: string | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ presets: Preset[] }>('/ai/presets').then((r) => setPresets(r.presets));
  }, []);

  const prefix = GROUP_PREFIX[contextKind];
  const filtered = useMemo(
    () =>
      prefix
        ? presets.filter((p) => p.id.startsWith(prefix) || p.id.startsWith('gen.'))
        : presets,
    [prefix, presets]
  );

  async function run(presetId?: string, freePrompt?: string, title?: string) {
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const r = await api.post<{
        answer: string;
        usage: { cost_usd: number };
        attached?: string | null;
      }>('/ai/ask', {
        preset: presetId,
        prompt: freePrompt,
        project_id: projectId,
        document_id: documentId,
      });
      setAnswer({
        text: r.answer,
        title: title ?? 'Réponse IA',
        costUsd: r.usage?.cost_usd ?? 0,
        attached: r.attached ?? null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur IA');
    } finally {
      setBusy(false);
    }
  }

  function onFreeSubmit(e: FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) return;
    run(undefined, prompt.trim(), `${contextLabel} — question libre`);
  }

  return (
    <div className="card">
      <div className="card-meta">Assistant IA · contexte</div>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>{contextLabel}</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {filtered.map((p) => (
          <button
            key={p.id}
            className="btn btn-ghost"
            style={{ padding: '6px 10px', fontSize: 12 }}
            disabled={busy}
            onClick={() => run(p.id, undefined, `${contextLabel} — ${p.label}`)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <form onSubmit={onFreeSubmit} style={{ marginTop: 12 }}>
        <textarea
          rows={2}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Question libre…"
          style={{
            width: '100%',
            padding: 8,
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--c-bg)',
            resize: 'vertical',
          }}
        />
        <button className="btn btn-primary" disabled={busy} style={{ marginTop: 6 }}>
          {busy ? 'Réflexion…' : 'Demander'}
        </button>
      </form>

      {error && <div className="error-msg" style={{ marginTop: 10 }}>{error}</div>}
      {answer && (
        <div style={{ marginTop: 12 }}>
          <AiAnswer
            title={answer.title}
            text={answer.text}
            costUsd={answer.costUsd}
            attached={answer.attached}
          />
        </div>
      )}
    </div>
  );
}
